import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { getCategoryById } from "../api/CategoryAPI";
import { getWhyTexts, sendWhyFeedback, WhyTexts } from "../api/ProspectAPI";
import { getAllUnits } from "../api/UnitAPI";
import { Category, CategoryParameter, ParameterDataType } from "../types/Category";
import { Unit } from "../types/Unit";
import DynamicForm from "./DynamicForm";
import CreateParametersForm from "./inventory/CreateParametersForm";
import { InventoryParameter } from "../types/Inventory";
import GeoAreaPicker from "./geo/GeoAreaPicker";
import { useDemoEmbedUrl } from "./DemoPage";

/** Minimal emergency texts, shown only until /public/why-texts answers (or
 *  when it is unreachable). NOT a copy of the real defaults — those live in
 *  exactly one place, the server's OutreachTemplateSeeder. */
const FALLBACK_POINTS =
    "<ul>"
    + "<li>Most listings are just text and cannot be searched precisely</li>"
    + "<li>Important details have to be discovered by reading the listing: {key_details}</li>"
    + "</ul>";
const FALLBACK_WHY_TEXTS: WhyTexts = {
    heading: "Your {category} listing: found, not buried",
    points: FALLBACK_POINTS,
    pointsService: FALLBACK_POINTS,
    pitch: "Most web searches match your words, not your meaning. BabaHanks is "
        + "different: every category has its own custom form, so searches return "
        + "exactly the item or service you want.",
    formNudge: "Please click the dropdowns below and see the choices for your "
        + "category already set up.",
    placeService: "For services, you define the areas you serve - cities, "
        + "counties, states, in any combination.",
    placeLocation: "For location-based categories - houses, apartments, rentals - "
        + "you set where the listing actually is.",
    sameForm: "When someone searches, they fill in the same category form, so "
        + "the search compares like with like.",
};

/** The why-page texts from the DB (Outreach template editor), with the
 *  built-in fallback until loaded / on failure. */
export function useWhyTexts(): WhyTexts {
    const [texts, setTexts] = useState<WhyTexts>(FALLBACK_WHY_TEXTS);
    useEffect(() => {
        getWhyTexts().then(setTexts).catch(() => {});
    }, []);
    return texts;
}

/** Fills {category}/{company} and tidies the holes unknown values leave
 *  ("Your  listing" -> "Your listing", stray separators trimmed). */
export function fillWhyTemplate(
    template: string,
    values: { category?: string | null; company?: string | null }
): string {
    return template
        .replace(/\{category\}/g, values.category?.trim() ?? "")
        .replace(/\{company\}/g, values.company?.trim() ?? "")
        .replace(/\s{2,}/g, " ")
        .replace(/^[\s:—-]+|[\s:—-]+$/g, "");
}

/** The prospect's current listing on a keyword site (from the public why DTO). */
export interface CurrentListing {
    url?: string | null;
    title?: string | null;
    imageUrl?: string | null;
    description?: string | null;
    /** Admin-uploaded full screenshot — preferred over the og preview. */
    screenshotUrl?: string | null;
}

/**
 * The keyword-site pain points, shown right under the page heading. Quotes
 * the category's own parameter names ("Home Type, Monthly Rent, ...") so the
 * prospect reads THEIR details, not generic ones.
 */
function escapeHtml(s: string): string {
    return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

/** Renders a points text: HTML when it contains markup, else the plain
 *  format of one bullet per line (converted to a list). Either way the
 *  result is sanitized (admin-authored, but the page is public) and
 *  {key_details} is substituted — it may itself be a nested <ul> of
 *  parameters and their choices. */
export function renderWhyPoints(source: string, keyDetails: string): React.ReactNode {
    let html: string;
    if (source.includes("<")) {
        html = source.replace(/\{key_details\}/g, keyDetails);
    } else {
        const bullets = source.split("\n").map((l) => l.trim()).filter((l) => l !== "");
        html = "<ul>"
            + bullets.map((b) => `<li>${escapeHtml(b).replace(/\{key_details\}/g, keyDetails)}</li>`).join("")
            + "</ul>";
    }
    return (
        <>
            <style>{`
                /* The page intro is grey (text-secondary) — the pain points
                   are the pitch, keep them full-strength. */
                .why-points { color: var(--text-primary, #1e293b); }
                /* index.css resets ul to list-style:none — restore ""Please click the dropdowns belowreal bullets here */
                .why-points ul { list-style: disc; margin: 0.15rem 0; padding-left: 1.2rem; }
                .why-points ul ul { list-style: circle; }
                .why-points > ul { margin: 0; }
                .why-points p { margin: 0 0 0.4rem; }
            `}</style>
            <div
                className="why-points"
                style={{ display: "inline-block", textAlign: "left", fontSize: "0.95rem", maxWidth: "640px" }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
            />
        </>
    );
}

/** How many of a choice parameter's options {key_details} spells out. */
const WHY_CHOICES_SHOWN = 5;

/** {key_details} as a bulleted list: one bullet per parameter, and for
 *  choice parameters up to WHY_CHOICES_SHOWN of its options as sub-bullets. */
function keyDetailsListHtml(params: CategoryParameter[], more: boolean): string {
    const items = params.map((p) => {
        let sub = "";
        if (p.dataType === ParameterDataType.SELECTABLE && p.options && p.options.length > 0) {
            const opts = p.options.slice(0, WHY_CHOICES_SHOWN).map((o) => `<li>${escapeHtml(o)}</li>`).join("");
            const tail = p.options.length > WHY_CHOICES_SHOWN ? "<li>…</li>" : "";
            sub = `<ul>${opts}${tail}</ul>`;
        }
        return `<li>${escapeHtml(p.name)}${sub}</li>`;
    }).join("");
    return `<ul>${items}${more ? "<li>…</li>" : ""}</ul>`;
}

export const WhyPainPoints: React.FC<{ category: Category | null; texts: WhyTexts }> = ({ category, texts }) => {
    const allParams = category?.parameters ?? [];
    const keyDetails = allParams.length > 0
        ? keyDetailsListHtml(allParams.slice(0, 10), allParams.length > 10)
        : "price, features, options";
    const source = category?.hasServiceArea ? texts.pointsService : texts.points;
    return <>{renderWhyPoints(source, keyDetails)}</>;
};

/**
 * The side-by-side comparison body: the prospect's ACTUAL current listing
 * (image + title, when we could fetch its preview) — or a generic keyword-
 * site mock — next to the category's real BabaHanks filters. Used by both
 * /why/c/{categoryId} (category only) and /why/p/{token} (personalized).
 */
export const WhyComparisonBody: React.FC<{
    category: Category | null;
    listing?: CurrentListing | null;
    /** Pre-fills the demo form's location picker (the prospect's own place). */
    initialLocation?: {
        countryId?: number | null;
        admin1Id?: number | null;
        admin2Id?: number | null;
        cityId?: number | null;
    } | null;
}> = ({ category, listing, initialLocation }) => {
    const name = category?.name ?? "your listings";
    const isService = !!category?.hasServiceArea;
    const params = category?.parameters ?? [];

    // Cold prospects give the page seconds, not minutes: both demo forms show
    // the first few parameters with a show-them-all expander, so each step
    // reads in about one screenful while nothing is more than a click away.
    const DEMO_ROWS = 6;
    const [allCreateParams, setAllCreateParams] = useState(false);
    const [allSearchParams, setAllSearchParams] = useState(false);
    const createParams = allCreateParams ? params : params.slice(0, DEMO_ROWS);
    const searchParams = allSearchParams ? params : params.slice(0, DEMO_ROWS);
    const expander = (expanded: boolean, toggle: (v: boolean) => void) =>
        params.length > DEMO_ROWS ? (
            <button
                type="button"
                className="btn-sm btn-secondary"
                style={{ marginTop: "0.4rem", fontWeight: 700 }}
                onClick={() => toggle(!expanded)}
            >
                {expanded
                    ? "Show fewer fields"
                    : `… and ${params.length - DEMO_ROWS} more fields — show them all`}
            </button>
        ) : null;
    const hasPreview = !!listing?.imageUrl || !!listing?.title;
    const hasScreenshot = !!listing?.screenshotUrl;

    // Units so numeric parameters show real unit names in the live form.
    const [units, setUnits] = useState<Unit[]>([]);
    useEffect(() => {
        getAllUnits("").then(setUnits).catch(() => setUnits([]));
    }, []);

    // The create-listing demo: name/link prefilled from the prospect's own
    // post once it loads; parameter values live only on this page.
    const [demoName, setDemoName] = useState("");
    const [demoUrl, setDemoUrl] = useState("");
    const [demoDescription, setDemoDescription] = useState("");
    const [demoValues, setDemoValues] = useState<Record<string, InventoryParameter>>({});
    useEffect(() => {
        if (listing?.title) setDemoName(listing.title);
    }, [listing?.title]);
    useEffect(() => {
        if (listing?.url) setDemoUrl(listing.url);
    }, [listing?.url]);
    useEffect(() => {
        if (listing?.description) setDemoDescription(listing.description);
    }, [listing?.description]);
    const onDemoValue = (pname: string, value: InventoryParameter | null) => {
        setDemoValues((v) => {
            const next = { ...v };
            if (value === null) delete next[pname]; else next[pname] = value;
            return next;
        });
    };

    // Full-width stacked steps (today → you create → they search): each
    // section gets the whole frame, so the forms have room to breathe.
    // Bold dark borders make the three steps read as distinct chapters.
    const panel: React.CSSProperties = {
        border: "2px solid var(--text-primary, #1e293b)",
        borderRadius: "8px",
        padding: "1rem",
        minWidth: 0,
    };
    // Inner boxes that sit side by side inside a step on wide screens.
    const innerRow: React.CSSProperties = { display: "flex", flexWrap: "wrap", gap: "0.6rem" };
    const innerBox: React.CSSProperties = {
        flex: "1 1 300px",
        minWidth: 0,
        border: "1px solid var(--border-color, #eee)",
        borderRadius: "6px",
        padding: "0.6rem",
    };

    return (
        <div className="why-panels" style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            {/* The embedded forms reuse the app's own components, whose
                0.85rem descriptions are too small for a pitch page — bump
                them up, here only. */}
            <style>{`
                .why-panels .inventory-parameter-row .desc-col { font-size: 0.9rem; }
                .why-panels .param-filters .pf-hint { font-size: 0.9rem; }
            `}</style>
            {/* Step 1 — today: their real listing when we have it, else
                keyword roulette. Capped width so a screenshot doesn't blow up. */}
            <div style={{ ...panel, background: "var(--bg-secondary, #f8f8f8)" }}>
                <h3 style={{ marginTop: 0 }}>1 — Your listing today</h3>
                <div style={{ maxWidth: "640px" }}>
                {hasScreenshot ? (
                    <div style={{ border: "1px solid var(--border-color, #ddd)", borderRadius: "6px", padding: "0.5rem", background: "#fff" }}>
                        <img
                            src={listing!.screenshotUrl!}
                            alt={listing?.title ?? "Your current listing"}
                            style={{ width: "100%", height: "auto", borderRadius: "4px", display: "block" }}
                        />
                        {listing?.url && (
                            <a href={listing.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: "0.85rem" }}>
                                see the full original post ↗
                            </a>
                        )}
                    </div>
                ) : hasPreview ? (
                    <div style={{ border: "1px solid var(--border-color, #ddd)", borderRadius: "6px", padding: "0.75rem", background: "#fff" }}>
                        {listing?.title && (
                            <div style={{ fontWeight: 600, fontSize: "1.05rem", marginBottom: "0.5rem" }}>{listing.title}</div>
                        )}
                        {listing?.imageUrl && (
                            <img
                                src={listing.imageUrl}
                                alt={listing.title ?? "Your current listing"}
                                style={{ width: "100%", height: "auto", borderRadius: "4px", display: "block" }}
                            />
                        )}
                        {listing?.description && (
                            <p style={{ whiteSpace: "pre-wrap", fontSize: "0.9rem", color: "#333", margin: "0.5rem 0 0.25rem" }}>
                                {listing.description}
                            </p>
                        )}
                        {listing?.url && (
                            <a href={listing.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: "0.85rem" }}>
                                see the full original post ↗
                            </a>
                        )}
                    </div>
                ) : (
                    <div style={{ border: "1px solid var(--border-color, #ddd)", borderRadius: "6px", padding: "0.5rem 0.75rem", background: "#fff", fontFamily: "monospace", fontSize: "0.85rem", color: "#444" }}>
                        <div>🔍 “{name.toLowerCase()}”</div>
                        <hr style={{ border: 0, borderTop: "1px solid #eee" }} />
                        <div>{name.toUpperCase()} CHEAP — CALL NOW!!!</div>
                        <div>best {name.toLowerCase()} dont miss ⭐⭐⭐⭐⭐</div>
                        <div>…and 400 more posts to read one by one</div>
                    </div>
                )}
                </div>
            </div>

            {/* The bridge from "today" to the BabaHanks steps. */}
            <div style={{ textAlign: "center", fontSize: "1.35rem", fontWeight: 700, margin: "0.5rem 0", color: "var(--text-primary, #1e293b)" }}>
                Please check out <Link to="/demo">the demo</Link> — but briefly, this is
                how listings are created and searched for on BabaHanks:
            </div>

            {/* Step 2 — the LISTER side: creating the listing. Name and link
                are prefilled from the prospect's own post; the area/location
                picker and the category's real create-form parameters are live
                to play with. */}
            <div style={panel}>
                <h3 style={{ marginTop: 0 }}>2 — On BabaHanks: you create your listing</h3>
                <div style={{ fontSize: "0.9rem", color: "var(--text-secondary)", marginBottom: "0.5rem" }}>
                    Your listing becomes real, searchable information — a name,
                    {isService ? " the area you serve," : " its location,"} and the
                    details, each a field of its own.
                    {(listing?.title || listing?.url) ? " We started it from your current post — try the rest:" : " Try it:"}
                </div>
                {/* No zoom here: CSS zoom breaks click hit-testing in Safari
                    (dropdowns "sometimes" not opening); the full-width layout
                    fits the forms at natural size. */}
                {/* Stacked in listing order: name/link, then the area, then
                    the category's parameters. */}
                <div>
                    <div style={{ ...innerBox, marginBottom: "0.6rem" }}>
                        <strong style={{ fontSize: "0.9rem" }}>Name, description, and your existing post</strong>
                        <div className="form-group" style={{ margin: "0.4rem 0 0.5rem" }}>
                            <label>Listing name</label>
                            <input
                                value={demoName}
                                placeholder={category ? `Your ${name} listing` : "Your listing"}
                                onChange={(e) => setDemoName(e.target.value)}
                            />
                        </div>
                        <div className="form-group" style={{ margin: "0 0 0.5rem" }}>
                            <label>Description — your own words, as long as you like</label>
                            <textarea
                                rows={3}
                                value={demoDescription}
                                placeholder="Describe your listing in your own words…"
                                onChange={(e) => setDemoDescription(e.target.value)}
                            />
                        </div>
                        <div className="form-group" style={{ margin: 0 }}>
                            <label>…or attach your own web page (your current post, your site)</label>
                            <input
                                value={demoUrl}
                                placeholder="https://…"
                                onChange={(e) => setDemoUrl(e.target.value)}
                            />
                            <div style={{ fontSize: "0.85rem", color: "var(--text-secondary)", marginTop: "0.2rem" }}>
                                The linked page is shown right inside your BabaHanks listing —
                                your existing post stays where it is.
                            </div>
                        </div>
                    </div>
                    <div style={{ ...innerBox, marginBottom: "0.6rem" }}>
                        <strong style={{ fontSize: "0.9rem" }}>
                            {isService ? "The areas you serve" : "Where this listing is located"}
                        </strong>
                        {isService && (
                            <div style={{ fontSize: "0.85rem", color: "var(--text-secondary)", marginTop: "0.15rem" }}>
                                You can have a combination of cities, counties, states, etc. —
                                anyone searching inside any of them finds you.
                            </div>
                        )}
                        <div style={{ marginTop: "0.4rem" }}>
                            <GeoAreaPicker
                                token=""
                                context={isService ? "provider" : "item"}
                                onChange={() => {}}
                                initial={initialLocation}
                            />
                        </div>
                    </div>
                    {category && params.length > 0 && (
                        <div style={innerBox}>
                            <strong style={{ fontSize: "0.9rem" }}>
                                Enter information about your listing using the category-specific form
                            </strong>
                            <div style={{ fontSize: "0.85rem", color: "var(--text-secondary)", marginTop: "0.15rem" }}>
                                If you need anything that is not here, please let us know and we
                                will add it to the form.
                            </div>
                            <div style={{ marginTop: "0.4rem" }}>
                                <CreateParametersForm
                                    parameters={createParams}
                                    units={units}
                                    values={demoValues}
                                    onChange={onDemoValue}
                                    headerExtra={
                                        <>
                                            <div style={{ fontWeight: 700, fontSize: "0.9rem", margin: "0.1rem 0" }}>
                                                Please click the dropdowns below — the choices for your
                                                category are already set up, just pick what applies.
                                            </div>
                                            {/* Aligned with the value column so the arrow sits
                                                right above the dropdowns themselves. */}
                                            <div className="inventory-parameter-row stacked-label" style={{ borderBottom: "none", padding: "0.1rem 0" }}>
                                                <div className="col name-col" />
                                                <div className="col value-col" style={{ fontWeight: 700, fontSize: "0.85rem", textAlign: "left" }}>
                                                    <div>please open these to see your options</div>
                                                    <div>⬇</div>
                                                </div>
                                            </div>
                                        </>
                                    }
                                />
                                {expander(allCreateParams, setAllCreateParams)}
                            </div>
                        </div>
                    )}
                </div>
                <div style={{ marginTop: "0.75rem", fontSize: "0.9rem" }}>
                    <strong>Every answer here is a real field — not words in your text</strong>
                    <div style={{ color: "var(--text-secondary)" }}>
                        {isService
                            ? "Search matches them directly: anyone searching inside your area, for what you offer, finds you."
                            : "Search matches them directly: a search for its city, county, or state — with these details — finds it."}
                    </div>
                </div>
            </div>

            {/* The ask, right when they've just seen how little work a real
                listing takes (print hides CTA rows). */}
            <div className="why-cta-row" style={{ textAlign: "center", margin: "0.25rem 0" }}>
                <Link
                    to="/register"
                    style={{
                        display: "inline-block",
                        background: "var(--primary-color, #4f46e5)", color: "#fff",
                        padding: "0.6rem 1.4rem", borderRadius: "8px",
                        fontWeight: 700, textDecoration: "none",
                    }}
                >
                    Create your account — listing takes just a few minutes
                </Link>
                <div style={{ fontSize: "0.85rem", color: "var(--text-secondary)", marginTop: "0.3rem" }}>
                    Free — and your current post stays right where it is.
                </div>
            </div>

            {/* Step 3 — the SEARCHER side: the REAL search form for this
                category: location first, then the category's parameters. */}
            <div style={panel}>
                <h3 style={{ marginTop: 0 }}>3 — How people search on BabaHanks</h3>
                <div style={{ fontSize: "0.9rem", color: "var(--text-secondary)", marginBottom: "0.5rem" }}>
                    This is the actual {category ? `${name} ` : ""}search form — try it.
                </div>
                <div style={innerRow}>
                    <div style={innerBox}>
                        <strong style={{ fontSize: "0.9rem" }}>
                            {isService ? "Where you need the service" : "Where you're looking"}
                        </strong>
                        <div style={{ marginTop: "0.4rem" }}>
                            <GeoAreaPicker
                                token=""
                                context={isService ? "searcher" : "itemSearcher"}
                                onChange={() => {}}
                                initial={initialLocation}
                            />
                        </div>
                    </div>
                    {category && params.length > 0 && (
                        <div style={innerBox}>
                            <strong style={{ fontSize: "0.9rem" }}>What you're looking for</strong>
                            <div style={{ fontWeight: 700, fontSize: "0.9rem", margin: "0.3rem 0 0.2rem" }}>
                                Please try the dropdowns — these are the exact choices
                                searchers pick from to find listings like yours.
                            </div>
                            <div style={{ marginTop: "0.4rem" }}>
                                <DynamicForm
                                    key={category.id}
                                    parameters={searchParams}
                                    categoryId={category.id}
                                    units={units}
                                    onSubmit={() => {}}
                                    formId="why-page-demo-form"
                                    hideSubmit
                                />
                                {expander(allSearchParams, setAllSearchParams)}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

/** "I get it" / "I don't get it", with an optional comment — the only way
 *  to learn whether the pitch lands. Public (prospects are signed out). */
export const WhyFeedbackBox: React.FC<{
    whyToken?: string | null;
    categoryId?: string | null;
}> = ({ whyToken, categoryId }) => {
    const [verdict, setVerdict] = useState<"GOT_IT" | "DONT_GET_IT" | null>(null);
    const [comment, setComment] = useState("");
    const [sent, setSent] = useState(false);
    const [busy, setBusy] = useState(false);

    const send = async () => {
        if (!verdict) return;
        setBusy(true);
        try {
            await sendWhyFeedback({ token: whyToken ?? undefined, categoryId: categoryId ?? undefined, verdict, comment });
        } catch {
            // a public pitch page never shows an error for feedback
        } finally {
            setSent(true);
            setBusy(false);
        }
    };

    if (sent) {
        return (
            <div className="why-feedback" style={{ textAlign: "center", margin: "1.25rem 0", fontWeight: 700 }}>
                Thank you — we read every note.
            </div>
        );
    }
    return (
        <div className="why-feedback" style={{ textAlign: "center", margin: "1.25rem 0" }}>
            <strong>Did this page make sense?</strong>
            <div style={{ display: "flex", gap: "0.6rem", justifyContent: "center", flexWrap: "wrap", margin: "0.5rem 0" }}>
                <button
                    type="button"
                    className={verdict === "GOT_IT" ? "" : "btn-secondary"}
                    onClick={() => setVerdict("GOT_IT")}
                >
                    👍 I get it
                </button>
                <button
                    type="button"
                    className={verdict === "DONT_GET_IT" ? "" : "btn-secondary"}
                    onClick={() => setVerdict("DONT_GET_IT")}
                >
                    🤔 I don't get it — what is this nonsense?
                </button>
            </div>
            {verdict && (
                <div style={{ maxWidth: "480px", margin: "0 auto" }}>
                    <textarea
                        rows={3}
                        value={comment}
                        placeholder={verdict === "GOT_IT"
                            ? "Anything you'd add or change? (optional)"
                            : "What lost you? Tell us straight — it helps. (optional)"}
                        onChange={(e) => setComment(e.target.value)}
                        disabled={busy}
                        style={{ width: "100%" }}
                    />
                    <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center", marginTop: "0.4rem" }}>
                        <button type="button" onClick={send} disabled={busy}>
                            {busy ? "Sending…" : "Send feedback"}
                        </button>
                        <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => { setVerdict(null); setComment(""); }}
                            disabled={busy}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

/** Shared page chrome for the why pages (topbar, heading, CTA buttons). */
export const WhyPageFrame: React.FC<{
    heading?: string;
    intro?: React.ReactNode;
    children: React.ReactNode;
    showGenericLink?: boolean;
    /** When set, a public "I get it / I don't get it" box renders above the
     *  CTAs, attributed to this page (prospect token or category). */
    feedback?: { token?: string | null; categoryId?: string | null };
}> = ({ heading, intro, children, showGenericLink, feedback }) => {
    const demoAvailable = !!useDemoEmbedUrl();
    // The top notice's inline feedback box (the bottom one always shows).
    const [topFeedbackOpen, setTopFeedbackOpen] = useState(false);

    // Personalized pages are private (token links) — keep them out of search
    // engines even if a link gets shared somewhere crawlable.
    useEffect(() => {
        const meta = document.createElement("meta");
        meta.name = "robots";
        meta.content = "noindex";
        document.head.appendChild(meta);
        return () => { document.head.removeChild(meta); };
    }, []);

    return (
        <div className="app-layout">
            {/* Printed material: hide the chrome, keep each panel on one page. */}
            <style>{`
                @media print {
                    .public-topbar, .why-cta-row, .why-generic-link, .why-feedback { display: none !important; }
                    .why-panels > div { break-inside: avoid; }
                }
            `}</style>
            <div className="public-topbar">
                <strong className="public-brand">BabaHanks</strong>
                <div className="public-links">
                    <Link to="/" className="btn-outline">Back to Search</Link>
                </div>
            </div>
            <div className="public-body">
                <div style={{ width: "100%", maxWidth: "1024px", margin: "1rem auto", padding: "0 0.5rem" }}>
                    {/* Feedback is the first thing they see: an honest reaction,
                        either way, is worth more to us than a silent close-tab.
                        The link opens the feedback box right here. */}
                    {feedback && (
                        <div className="why-feedback" style={{ textAlign: "center", fontSize: "0.9rem", marginBottom: "0.75rem" }}>
                            We'd love your honest reaction to this page — 👍 or 🤔,{" "}
                            <button
                                type="button"
                                onClick={() => setTopFeedbackOpen((o) => !o)}
                                style={{
                                    background: "none", border: "none", padding: 0, cursor: "pointer",
                                    color: "var(--primary-color, #4f46e5)", textDecoration: "underline",
                                    font: "inherit", fontWeight: 700,
                                }}
                            >
                                leave feedback here
                            </button>
                            {" "}(takes ten seconds).
                        </div>
                    )}
                    {feedback && topFeedbackOpen && (
                        <WhyFeedbackBox whyToken={feedback.token} categoryId={feedback.categoryId} />
                    )}
                    {heading && <h2 style={{ textAlign: "center" }}>{heading}</h2>}
                    {intro && (
                        <div style={{ textAlign: "center", color: "var(--text-secondary)", maxWidth: "680px", margin: "0.5rem auto 1.5rem" }}>
                            {intro}
                        </div>
                    )}
                    {children}
                    {feedback && <WhyFeedbackBox whyToken={feedback.token} categoryId={feedback.categoryId} />}
                    <div className="why-cta-row" style={{ display: "flex", gap: "0.75rem", justifyContent: "center", margin: "1.5rem 0", flexWrap: "wrap" }}>
                        <Link to="/" className="btn-outline">Try a search</Link>
                        {demoAvailable && <Link to="/demo" className="btn-outline">▶ Watch the demo</Link>}
                        <Link to="/register" className="btn-outline">Create your account</Link>
                    </div>
                    {showGenericLink && (
                        <p className="why-generic-link" style={{ textAlign: "center", color: "var(--text-secondary)", fontSize: "0.85rem" }}>
                            <Link to="/why">See the full comparison</Link>
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

/** /why/c/{categoryId}: category-personalized comparison (no prospect data). */
const WhyCategoryPage: React.FC = () => {
    const { categoryId } = useParams<{ categoryId: string }>();
    const [category, setCategory] = useState<Category | null>(null);
    const [failed, setFailed] = useState(false);
    const texts = useWhyTexts();

    useEffect(() => {
        if (!categoryId) { setFailed(true); return; }
        getCategoryById("", categoryId)
            .then(setCategory)
            .catch(() => setFailed(true)); // page still renders, generically
    }, [categoryId]);

    return (
        <WhyPageFrame
            heading={fillWhyTemplate(texts.heading, { category: category?.name }) || "Found, not buried"}
            intro={<WhyPainPoints category={category} texts={texts} />}
            showGenericLink={failed}
            feedback={{ categoryId }}
        >
            <WhyComparisonBody category={category} />
        </WhyPageFrame>
    );
};

export default WhyCategoryPage;